import {
    createStyles,
    DialogContent,
    makeStyles,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from "@material-ui/core";
import { RewardsDetails } from "core/src/domain/rewardDetails";
import React from "react";

import { Dialog, DialogTitle, RelodeTheme, Text } from "..";
import { JobType } from "../../domain";
import { baseColors } from "../styles";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            tableHeader: {
                fontWeight: "bolder",
            },
            cellHeader: {
                color: theme.relode.colors.pageSecondaryColor,
                paddingBottom: 4,
            },
            totalsRow: {
                backgroundColor: theme.relode.colors.pageSecondaryBackground,
            },
            totalsAll: {
                textAlign: "center",
            },
            totalsHeader: {
                color: theme.relode.colors.pageSecondaryColor,
                fontSize: 16,
                marginTop: 20,
            },
            totalsAmount: {
                color: baseColors.purple,
                fontSize: 30,
                fontWeight: "bold",
                fontStyle: "italic",
                fontFamily: "'hkgrotesk_bolditalic',Helvetica,sans-serif",
                lineHeight: "32px",
                padding: theme.spacing(1),
            },
            paymentDetailsHeader: {
                color: theme.relode.colors.pagePrimaryColor,
                fontSize: 24,
                fontWeight: "bold",
                margin: theme.spacing(2, 0),
            },
            paymentDetailsBody: {
                margin: theme.spacing(2, 0),
            },
            paymentDetails: {
                margin: theme.spacing(5, 0, 0, 0),
            },
            promotionRow: {
                backgroundColor: theme.relode.colors.rowHighlight.main,
            },
        }),
    { name: "RewardsViewModal" }
);

interface RewardsViewModalProps {
    open: boolean;
    onClose: () => void;
    rewardsDetails: RewardsDetails;
    jobTypeId: number;
    hideManagementRewards?: boolean;
    isCandidateHired: boolean;
}

export const RewardsViewModal = (props: RewardsViewModalProps) => {
    const {
        open,
        onClose,
        rewardsDetails,
        jobTypeId,
        hideManagementRewards,
        isCandidateHired,
    } = props;
    const classes = useStyles(props);

    let paymentDetailsText = null;
    let disclaimerSubtext = null;
    let employmentGuaranteeDetails = null;

    const hiredRewardsAdornment = isCandidateHired ? "" : "*";

    const paymentDetailsPrefix = isCandidateHired ? null : (
        <>
            Rewards are subject to change at Relode's discretion.
            <br />
            <br />
        </>
    );

    if (JobType.isFullTimeOrPartTime(jobTypeId)) {
        paymentDetailsText = (
            <>
                <Text variant="body1">
                    {paymentDetailsPrefix}
                    The submission and interview rewards are paid out one day
                    after the system status has been updated.
                    <br />
                    <br />
                    The hire reward is paid out as follows:
                </Text>
            </>
        );
        employmentGuaranteeDetails = (
            <>
                <Text variant="h5">
                    {rewardsDetails.employmentGuaranteeDescription} guarantee{" "}
                    <br />
                    {rewardsDetails.employmentGuaranteePayment} payment
                    installments{" "}
                </Text>
                <br />
            </>
        );
        disclaimerSubtext = (
            <Text variant="body1">
                * The hire reward amount is estimated based on the job salary.
                The reward paid will be based on actual salary for the hired
                candidate.
            </Text>
        );
    } else if (JobType.isContractOrLocum(jobTypeId)) {
        paymentDetailsText = (
            <>
                <Text variant="body1">
                    {paymentDetailsPrefix}
                    The submission and interview rewards are paid out one day
                    after the system status has been updated.
                </Text>
                <br />
            </>
        );
        disclaimerSubtext = (
            <Text variant="body1">
                * The hire reward is estimated based on the margin using the pay
                rate and number of weeks from the contract start date to its end
                date and is paid based on actual hours worked. The pay rate has
                a direct impact on the margin thus impacting the reward amounts.
                For example, a higher pay rate for the contractor will result in
                lower rewards, due to the decrease in margin. The reward will be
                paid proportionally on a bi-weekly basis, as soon as Relode
                receives the approved time sheets from the client.
            </Text>
        );
    }

    const convertNullValueToDollarString = (rewardAmount: string) =>
        rewardAmount ?? "$0.00";

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs">
            <DialogTitle title="reward details" onClose={onClose} />
            <DialogContent>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell
                                className={classes.tableHeader}
                                align="center"
                            >
                                referred
                            </TableCell>
                            {!hideManagementRewards ? (
                                <TableCell
                                    className={classes.tableHeader}
                                    align="center"
                                >
                                    managed
                                </TableCell>
                            ) : null}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell align="center">
                                <div className={classes.cellHeader}>
                                    submission
                                </div>
                                {convertNullValueToDollarString(
                                    rewardsDetails.submissionReferralCommissionInUSDollars
                                )}
                            </TableCell>
                            {!hideManagementRewards ? (
                                <TableCell align="center">
                                    <div className={classes.cellHeader}>
                                        submission
                                    </div>
                                    {convertNullValueToDollarString(
                                        rewardsDetails.submissionManagementCommissionInUSDollars
                                    )}
                                </TableCell>
                            ) : null}
                        </TableRow>

                        <TableRow>
                            <TableCell align="center">
                                <div className={classes.cellHeader}>
                                    interview
                                </div>
                                {convertNullValueToDollarString(
                                    rewardsDetails.interviewReferralCommissionInUSDollars
                                )}
                            </TableCell>
                            {!hideManagementRewards ? (
                                <TableCell align="center">
                                    <div className={classes.cellHeader}>
                                        interview
                                    </div>
                                    {convertNullValueToDollarString(
                                        rewardsDetails.interviewManagementCommissionInUSDollars
                                    )}
                                </TableCell>
                            ) : null}
                        </TableRow>
                        <TableRow>
                            <TableCell align="center">
                                <div className={classes.cellHeader}>hire</div>
                                {`${convertNullValueToDollarString(
                                    rewardsDetails.hireReferralCommissionInUSDollars
                                )}${hiredRewardsAdornment}`}
                            </TableCell>
                            {!hideManagementRewards ? (
                                <TableCell align="center">
                                    <div className={classes.cellHeader}>
                                        hire
                                    </div>
                                    {`${convertNullValueToDollarString(
                                        rewardsDetails.hireManagementCommissionInUSDollars
                                    )}${hiredRewardsAdornment}`}
                                </TableCell>
                            ) : null}
                        </TableRow>
                        {rewardsDetails.isPromotionActive &&
                        (!!rewardsDetails.hireReferralPromotionCommissionInUSDollars ||
                            !!rewardsDetails.hireManagementPromotionCommissionInUSDollars) ? (
                            <TableRow className={classes.promotionRow}>
                                <TableCell align="center">
                                    <div className={classes.cellHeader}>
                                        hire promotion
                                    </div>
                                    {`${convertNullValueToDollarString(
                                        rewardsDetails.hireReferralPromotionCommissionInUSDollars
                                    )}${hiredRewardsAdornment}`}
                                </TableCell>
                                {!hideManagementRewards ? (
                                    <TableCell align="center">
                                        <div className={classes.cellHeader}>
                                            hire promotion
                                        </div>
                                        {`${convertNullValueToDollarString(
                                            rewardsDetails.hireManagementPromotionCommissionInUSDollars
                                        )}${hiredRewardsAdornment}`}
                                    </TableCell>
                                ) : null}
                            </TableRow>
                        ) : null}
                        <TableRow className={classes.totalsRow}>
                            <TableCell align="center">
                                <div className={classes.cellHeader}>
                                    subtotal
                                </div>
                                {convertNullValueToDollarString(
                                    rewardsDetails.totalReferralCommissionInUSDollars
                                )}
                            </TableCell>
                            {!hideManagementRewards ? (
                                <TableCell align="center">
                                    <div className={classes.cellHeader}>
                                        subtotal
                                    </div>
                                    {convertNullValueToDollarString(
                                        rewardsDetails.totalManagementCommissionInUSDollars
                                    )}
                                </TableCell>
                            ) : null}
                        </TableRow>
                    </TableBody>
                </Table>

                <div className={classes.totalsAll}>
                    <div className={classes.totalsHeader}>total</div>
                    <Text className={classes.totalsAmount}>
                        {convertNullValueToDollarString(
                            rewardsDetails.totalCommissionInUSDollars
                        )}
                    </Text>
                </div>

                <div className={classes.paymentDetails}>
                    <Text
                        component="h2"
                        className={classes.paymentDetailsHeader}
                    >
                        payment details
                    </Text>
                    <Text className={classes.paymentDetailsBody}>
                        {paymentDetailsText}
                        {employmentGuaranteeDetails}
                        {disclaimerSubtext}
                    </Text>
                </div>
            </DialogContent>
        </Dialog>
    );
};
